import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { InventoryList } from "../components/InventoryList";
import { ScopePicker, type Scope } from "../components/ScopePicker";
import { money } from "../lib/format";
import { useInventoryList } from "../lib/useInventoryList";

interface Summary {
  inventory: {
    activeUnits: number;
    listedUnits: number;
    unlistedUnits: number;
    sold30d: number;
    writtenOffUnits?: number;
  } | null;
  totals: {
    capitalSpent: string | null;
    inventoryValue: string | null;
    totalProfit: string | null;
    totalRevenue: string | null;
    /** Purchase price of damaged / donated / lost items — already netted out of totalProfit. */
    writtenOff?: string | null;
  } | null;
  listingsByPlatform: Array<{ platform: string; count: number }>;
}

export function Dashboard() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [needle, setNeedle] = useState("");
  const [scope, setScope] = useState<Scope>("business");
  const [personalCount, setPersonalCount] = useState<number | undefined>(undefined);
  const list = useInventoryList(scope, needle, null);
  const { total } = list;
  const error = summaryError ?? list.error;

  // Typing shouldn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setNeedle(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Every figure is read through the same scope as the list below it, so the
  // tiles can never disagree with what is on screen.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const s = await api.get<Summary>(`/items/summary?scope=${scope}`);
        if (!cancelled) setSummary(s);
      } catch (err) {
        if (!cancelled) setSummaryError(err instanceof Error ? err.message : "Could not load your inventory");
      }
    })();
    return () => { cancelled = true; };
  }, [scope]);

  // Only to badge the Personal tab with a count; kept out of the main load so a
  // failure here cannot break the page.
  useEffect(() => {
    void (async () => {
      try {
        const r = await api.get<{ total: number }>(
          "/items?status=draft,in_stock,listed&limit=1&scope=personal"
        );
        setPersonalCount(r.total);
      } catch { /* the tab just goes unbadged */ }
    })();
  }, [total]);

  const profit = Number(summary?.totals?.totalProfit ?? 0);
  const writtenOff = Number(summary?.totals?.writtenOff ?? 0);
  const writtenOffUnits = summary?.inventory?.writtenOffUnits ?? 0;
  const listed = summary?.inventory?.listedUnits ?? 0;
  const notListed = summary?.inventory?.unlistedUnits ?? 0;
  const byPlatform = summary?.listingsByPlatform ?? [];

  const listTitle = needle ? `Matches (${total})` : `On hand (${total})`;

  return (
    <div className="shell">
      <header className="masthead">
        <div>
          <span className="wordmark">thrifty<span>business</span></span>
          <p className="slogan">Giving clothes a second chance</p>
        </div>
        <Link to="/intake"><button>Add a find</button></Link>
      </header>

      {error && <div className="notice">{error}</div>}

      <ScopePicker scope={scope} onChange={setScope} personalCount={personalCount} />

      <section className="ledger">
        <div>
          <span className="eyebrow">Capital spent</span>
          <span className="value">{money(summary?.totals?.capitalSpent)}</span>
        </div>
        <div>
          <span className="eyebrow">Inventory value</span>
          <span className="value">{money(summary?.totals?.inventoryValue)}</span>
        </div>
        <div>
          <span className="eyebrow">Total profit</span>
          <span
            className={`value ${profit >= 0 ? "pos" : "neg"}`}
            title={
              writtenOff > 0
                ? `After ${money(writtenOff)} written off (${writtenOffUnits} damaged, donated or lost)`
                : undefined
            }
          >
            {money(profit)}
          </span>
          {writtenOff > 0 && (
            <span className="hint" style={{ margin: 0 }}>
              incl. {money(-writtenOff)} written off
            </span>
          )}
        </div>
        <div>
          <span className="eyebrow">Listed / not listed</span>
          <span className="value">{listed} <span className="muted-slash">/</span> {notListed}</span>
        </div>
      </section>

      <section className="rail">
        <h2 className="eyebrow rail-label">Listings per site</h2>
        {byPlatform.length === 0 ? (
          <p className="hint">Nothing is actively listed yet.</p>
        ) : (
          <div className="breakdown">
            {byPlatform.map((p) => (
              <div key={p.platform} className="breakdown-row">
                <span className="breakdown-label">{p.platform}</span>
                <span className="breakdown-value">{p.count}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <InventoryList
        list={list}
        title={listTitle}
        q={q}
        onSearch={setQ}
        filtering={!!needle}
        onClear={() => setQ("")}
      />
    </div>
  );
}
